import { Validators, ValidatorFn, AbstractControl, FormBuilder, FormGroup, ValidationErrors } from "@angular/forms";
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from "@angular/core";

const MAX_WEIGHT = 50; // kg
const MAX_DIMENSION_SIDE = 120; // cm (cada lado)
const MAX_DIMENSION_SUM = 200; // cm (soma altura+largura+comprimento)
const MAX_DECLARED_VALUE = 10000; // BRL

function weightValidator(): ValidatorFn {
  return (control: AbstractControl) => {
    const value = control.value;
    if (!value) {
      return null;
    }

    const numericValue = parseFloat(value.toString().replace(',', '.'));
    return numericValue > MAX_WEIGHT ? { maxWeight: { actual: numericValue, max: MAX_WEIGHT } } : null;
  };
}

function dimensionSideValidator(): ValidatorFn {
  return (control: AbstractControl) => {
    const value = control.value;
    if (!value) {
      return null;
    }

    const numericValue = parseInt(value.toString(), 10);
    return numericValue > MAX_DIMENSION_SIDE ? { maxDimensionSide: { actual: numericValue, max: MAX_DIMENSION_SIDE } } : null;
  };
}

function declaredValueValidator(): ValidatorFn {
  return (control: AbstractControl) => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const numericValue = toNumberLocale(value);
    return numericValue > MAX_DECLARED_VALUE ? { maxDeclaredValue: { actual: numericValue, max: MAX_DECLARED_VALUE } } : null;
  };
}

function toNumberLocale(value: any): number {
  if (value === null || value === undefined) { return 0; }
  if (typeof value === 'number') { return value; }
  let text = value.toString().trim();
  if (!text) { return 0; }
  text = text.replace(/[^0-9.,-]/g, '');
  if (text.indexOf(',') > -1 && text.indexOf('.') > -1) {
    text = text.replace(/\./g, '').replace(',', '.');
  } else if (text.indexOf(',') > -1) {
    text = text.replace(',', '.');
  }
  const parsed = parseFloat(text);
  return isNaN(parsed) ? 0 : parsed;
}

function dimensionsSumValidator(): (group: AbstractControl) => ValidationErrors | null {
  return (group: AbstractControl) => {
    const alturaCtrl = group.get('altura');
    const larguraCtrl = group.get('largura');
    const comprimentoCtrl = group.get('comprimento');

    const altura = alturaCtrl ? alturaCtrl.value : null;
    const largura = larguraCtrl ? larguraCtrl.value : null;
    const comprimento = comprimentoCtrl ? comprimentoCtrl.value : null;

    if (altura == null || largura == null || comprimento == null) {
      return null;
    }

    const a = parseInt(altura.toString(), 10) || 0;
    const l = parseInt(largura.toString(), 10) || 0;
    const c = parseInt(comprimento.toString(), 10) || 0;
    const sum = a + l + c;
    return sum > MAX_DIMENSION_SUM ? { maxDimensionSum: { actual: sum, max: MAX_DIMENSION_SUM } } : null;
  };
}


@Component({
  selector: "mandae-simulador-frete-produto",
  templateUrl: "./produto.component.html",
  styleUrls: ["./produto.component.scss"],
})
export class SimuladorFreteProdutoComponent implements OnDestroy {
  public formulario: FormGroup;

  public indice = 0;

  public initial = false;

  public position = 0;

  public readonly MAX_WEIGHT = MAX_WEIGHT;
  public readonly MAX_DIMENSION_SIDE = MAX_DIMENSION_SIDE;
  public readonly MAX_DIMENSION_SUM = MAX_DIMENSION_SUM;
  public readonly MAX_DECLARED_VALUE = MAX_DECLARED_VALUE;

  @Input()
  public set Indice(indice: number) {
    this.position = indice;
    this.indice = indice === 0 ? 2 : indice + 2;
  }

  @Input()
  public set Initial(initial: boolean) {
    this.initial = initial;

    if (this.initial) {
      this.indice = 1;
    }
  }

  @Output()
  public produtoResponse = new EventEmitter();

  constructor(private readonly formBuilder: FormBuilder) {
    this.formulario = this.formBuilder.group({
      peso: [null, [Validators.required, weightValidator()]],
      altura: [null, [Validators.required, dimensionSideValidator()]],
      largura: [null, [Validators.required, dimensionSideValidator()]],
      comprimento: [null, [Validators.required, dimensionSideValidator()]],
      quantidade: [null, Validators.required],
      valor: [null, [declaredValueValidator()]], // Removido Validators.required
    }, { validators: [dimensionsSumValidator()] });

    // Observar mudanças no formulário para emitir eventos de mudança
    this.formulario.valueChanges.subscribe(() => {
      this.emitChange();
    });
  }

  private emitChange() {
    const data = {
      weight: this.formulario.get("peso").value,
      height: this.formulario.get("altura").value,
      width: this.formulario.get("largura").value,
      length: this.formulario.get("comprimento").value,
      quantity: this.formulario.get("quantidade").value,
      declaredValue: this.formulario.get("valor").value,
      indice: this.position,
      initial: this.initial,
      isValid: this.formulario.valid
    };
    this.produtoResponse.emit({ action: "adicionar", data });
    this.produtoResponse.emit({ action: "change", data: { indice: this.position } });
  }

  public ngOnDestroy() {
    this.formulario.get("peso").setValue(null);
    this.formulario.get("altura").setValue(null);
    this.formulario.get("largura").setValue(null);
    this.formulario.get("comprimento").setValue(null);
    this.formulario.get("quantidade").setValue(null);
    this.formulario.get("valor").setValue(null);
  }

  public removerProduto() {
    this.produtoResponse.emit({ action: "remover", data: this.position });
  }

  public handleChange(event: any) {
    // O método emitChange já é chamado automaticamente pelo valueChanges
    // Mantemos este método para compatibilidade com eventos manuais se necessário
    this.emitChange();
  }

  public formData(value) {
    return this.formulario.get(value);
  }

  public hasWeightError(fieldName: string): boolean {
    const field = this.formulario.get(fieldName);
    return field && field.touched && field.hasError('maxWeight');
  }

  public hasDimensionError(fieldName: string): boolean {
    const field = this.formulario.get(fieldName);
    return field && field.touched && field.hasError('maxDimension');
  }

  public getErrorMessage(fieldName: string): string {
    const field = this.formulario.get(fieldName);
    if (!field || !field.errors) {
      return '';
    }

    if (field.hasError('required')) {
      return 'Campo obrigatório';
    }

    if (field.hasError('maxWeight')) {
      return `Peso não pode ser maior que ${MAX_WEIGHT}kg`;
    }

    if (field.hasError('maxDimensionSide')) {
      return `Dimensão não pode ser maior que ${MAX_DIMENSION_SIDE}cm`;
    }

    if (field.hasError('maxDeclaredValue')) {
      return `Valor declarado não pode ser maior que ${MAX_DECLARED_VALUE}`;
    }

    return '';
  }

  public validarDados() {
    if (!this.formData("peso").value) {
      this.formData("peso").setErrors({ required: true });
      return false;
    }
    if (!this.formData("altura").value) {
      this.formData("altura").setErrors({ required: true });
      return false;
    }
    if (!this.formData("largura").value) {
      this.formData("largura").setErrors({ required: true });
      return false;
    }
    if (!this.formData("comprimento").value) {
      this.formData("comprimento").setErrors({ required: true });
      return false;
    }
    if (!this.formData("quantidade").value) {
      this.formData("quantidade").setErrors({ required: true });
      return false;
    }
    // Valor declarado é opcional - não validar como obrigatório
    return true;
  }
}
